// Created by jarongallo at 3/31/21
import React, { useEffect, useState } from 'react';
import UIHover from '../../../../../components/UI/UIHover/UIHover';
import { CancelIcon, PlusIcon } from '../../../../../assets/icons';
import EmptyDataSet from '../../../../../components/PL/EmptyDataSet/EmptyDataSet';
import UIIcon from '../../../../../components/UI/UIIcon';
import UIModal from '../../../../../components/UI/UIModal';
import UIOverlayCard from '../../../../../components/UI/UIOverlayCard';
import UIUserSearch from '../../../../../components/UI/UIUserSearch/UIUserSearch';
import { USER_ROLE_PHOTOGRAPHER } from '../../../../../utils/Config/Constants';
import UIIconButton from '../../../../../components/UI/UIIconButton';
import Queries from '../../../../../utils/API/Queries';
import { useApolloClient } from 'react-apollo';
import { toast } from 'react-toastify';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_AGENT_FOR_BLOCKED_TECHNICIANS } from './queries';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../../../../components/UI/UIIconButton/config';

export default function IndividualAgentBlockedTechs({ agentId }: IndividualAgentBlockedTechsProps) {
	const [agent, setAgent] = useState<UserType>();
	const [showAddBlockedTechnicianModal, setShowAddBlockedTechnicianModal] = useState<boolean>(false);

	const [technicianToUnblock, setTechnicianToUnblock] = useState<UserType>();
	const [technicianToBlock, setTechnicianToBlock] = useState<UserType>();

	const [isBlockingTechnician, setIsBlockingTechnician] = useState<boolean>(false);
	const [isUnblockingPhotographer, setIsUnblockingPhotographer] = useState<boolean>(false);

	const client = useApolloClient();

	const [getAgent, { data: agentData }] = useLazyQuery(GET_AGENT_FOR_BLOCKED_TECHNICIANS, {
		variables: { id: agentId }
	});

	useEffect(() => {
		(async () => {
			await getAgent();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (agentData) {
				const fetchedAgent: UserType = agentData['user'];
				setAgent(fetchedAgent);
			}
		})();
	}, [agentData]);

	if (!agent) return null;

	const blockTechnician = async () => {
		if (!isBlockingTechnician) {
			if (!technicianToBlock) {
				toast.error('No technician selected!');
				return;
			}
			setIsBlockingTechnician(true);

			const response = await client.mutate({
				mutation: Queries.UPDATE_USER_MUTATION,
				variables: { id: agentId, optOutPhotographers: [...agent.optOutPhotographers.map((technician) => technician.id), technicianToBlock.id] }
			});
			const { updateUser } = response['data'];
			if (updateUser) {
				await getAgent();
				toast.success('Successfully blocked photographer!');
			} else {
				toast.success('Error blocking photographer');
			}
			setIsBlockingTechnician(false);
			setTechnicianToBlock(undefined);
			setShowAddBlockedTechnicianModal(false);
		}
	};

	const renderAddBlockedTechnicianModal = () => {
		const hideModal = () => {
			setShowAddBlockedTechnicianModal(false);
			setTechnicianToBlock(undefined);
		};

		return (
			<UIModal condition={showAddBlockedTechnicianModal}>
				<UIOverlayCard
					hideModal={hideModal}
					title={'Block a technician'}
					cardStyle={{ height: '100%' }}
					description={'This will block the technician from seeing any services booked by this agent.'}>
					<UIUserSearch
						emptySetText={'No technicians found with that name'}
						role={USER_ROLE_PHOTOGRAPHER}
						selectedUser={technicianToBlock}
						placeholder={'Technician to block...'}
						onSelectUser={setTechnicianToBlock}
					/>
					<div style={{ marginTop: 16 }} className='uiCard__buttons'>
						<UIIconButton
							dark
							isDisabled={!technicianToBlock}
							disabledErrorMessage={'Please select a technician to block.'}
							type={UI_ICON_BUTTON_FILL_TYPE}
							isLoading={isBlockingTechnician}
							text={'block technician'}
							onClick={blockTechnician}
						/>
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	const unblockTechnician = async () => {
		if (!isUnblockingPhotographer && technicianToUnblock) {
			setIsUnblockingPhotographer(true);

			let optOutPhotographers = Object.assign(
				[],
				agent.optOutPhotographers.map((agent) => agent.id)
			);
			let index = optOutPhotographers.indexOf(technicianToUnblock.id);
			if (index > -1) {
				optOutPhotographers.splice(index, 1);
			}

			const response = await client.mutate({
				mutation: Queries.UPDATE_USER_MUTATION,
				variables: { id: agentId, optOutPhotographers }
			});
			const { updateUser } = response['data'];
			if (updateUser) {
				await getAgent();
				setIsUnblockingPhotographer(false);
				setTechnicianToUnblock(undefined);
				toast.success('Successfully unblocked photographer!');
			}
		}
	};

	const renderBlockedPhotographerModal = () => {
		const hideModal = () => {
			setTechnicianToUnblock(undefined);
		};

		return (
			<UIModal condition={technicianToUnblock !== undefined}>
				{!technicianToUnblock ? undefined : (
					<UIOverlayCard
						hideModal={hideModal}
						title={`Unblock ${technicianToUnblock.name}?`}
						description={`This will allow ${technicianToUnblock.name} to perform services for ${agent.name}.`}>
						<div className='uiCard__buttons' style={{ margin: '16px 0 0 0' }}>
							<UIIconButton
								dark
								type={UI_ICON_BUTTON_FILL_TYPE}
								isLoading={isUnblockingPhotographer}
								text={`yes, unblock ${technicianToUnblock.name}.`}
								onClick={unblockTechnician}
							/>
						</div>
					</UIOverlayCard>
				)}
			</UIModal>
		);
	};

	return (
		<div className='agentPage'>
			<div className='individualAgent__card--blocked-heading'>
				<p className='page-title'>Blocked technicians</p>
				<UIHover text={'block a photographer'}>
					<img
						style={{
							width: 28,
							height: 28,
							backgroundColor: 'var(--mint-primary)',
							borderRadius: '50%',
							padding: 4,
							marginLeft: 12,
							cursor: 'pointer'
						}}
						src={PlusIcon.white}
						alt={'add'}
						onClick={() => setShowAddBlockedTechnicianModal(true)}
					/>
				</UIHover>
			</div>
			<div className='pl-separator' />
			{agent.optOutPhotographers ? (
				<div className='individualAgent__card--blocked-photographers'>
					{agent.optOutPhotographers.length === 0 ? (
						<EmptyDataSet message={'This agent has no blocked photographers.'} />
					) : (
						agent.optOutPhotographers.map((blockedPhotographer) => (
							<div className='blockedPhotographers' key={blockedPhotographer.id}>
								<img className='blockedPhotographers__img' alt={'photographer'} src={blockedPhotographer.image} />
								<p className='blockedPhotographers__name'>{blockedPhotographer.name}</p>
								<div className='blockedPhotographers__icon' onClick={() => setTechnicianToUnblock(blockedPhotographer)}>
									<UIIcon className='blockedPhotographers__icon--img' alt='edit' iconSrc={CancelIcon.white} />
								</div>
							</div>
						))
					)}
				</div>
			) : (
				<div style={{ marginTop: 16 }}>
					<p>Loading...</p>
				</div>
			)}
			{renderAddBlockedTechnicianModal()}
			{renderBlockedPhotographerModal()}
		</div>
	);
}
